/**
 * Server-injected feature flags. The set is written into the page before the
 * app boots — `_allAppsCommonHeadSection.cshtml` in the web role, a rendered
 * token in the Mac dev server — so these reads are synchronous by design.
 *
 * A key can legitimately be ABSENT rather than false: flag rows are created
 * lazily by the AdminPortal's well-known-flag ensure, so between a deploy and
 * an admin first opening the flags page there is no row. `whenUndefined` is how
 * a caller says which way that should fall.
 */
export function gpHasFeature(flagName: string, whenUndefined: boolean = false): boolean {
    if (typeof __gameplan_feature_flags !== 'object' || !__gameplan_feature_flags) {
        return whenUndefined;
    }
    const value = __gameplan_feature_flags[flagName];
    return value === undefined ? whenUndefined : !!value;
}

const AI_FEATURES_V1 = 'AiFeaturesV1';
const AI_FEATURES_V2 = 'AiFeaturesV2';

/**
 * Whether ANY AI feature is available: the surfaces that shipped before the
 * flags existed (AI Planner, Expand with AI, the intelligence tab and its
 * report history) as well as the new wave. V2 supersedes V1, so either is
 * enough.
 *
 * V1 fails OPEN — an undefined flag means enabled — because the live AI
 * surfaces must keep working in the window before any flag row exists.
 */
export function isAnyAiFeatureFlagEnabled(): boolean {
    return gpHasFeature(AI_FEATURES_V1, true) || gpHasFeature(AI_FEATURES_V2);
}

/**
 * Whether the NEW wave of AI is available: chat, file uploads, and per-tenant
 * AI credits with the credits UI. Fails CLOSED — none of it has shipped, so an
 * undefined flag means off.
 */
export function isAiV2FeatureFlagEnabled(): boolean {
    return gpHasFeature(AI_FEATURES_V2);
}

const AI_DICTATION = 'AiDictation';

/**
 * Whether voice dictation is available: the mic button on the AI chat composer
 * and the get-started plan prompt (issue #403).
 *
 * Sits INSIDE the V2 wave — it is an AND, not an OR, so enabling dictation can
 * never open a door into V2 for a tenant that does not have it. Fails CLOSED
 * for the same reason AiFeaturesV2 does: unreleased.
 */
export function isAiDictationEnabled(): boolean {
    return isAiV2FeatureFlagEnabled() && gpHasFeature(AI_DICTATION);
}

const GET_STARTED_DIALOG = 'GetStartedDialog';
const PLAN_REVEAL_ANIMATION = 'PlanRevealAnimation';

/**
 * Whether the get-started dialog opens when an editor opens an empty plan.
 * The feature shipped ungated (PR #192) and was pulled behind this flag so
 * its visibility is an AdminPortal decision. Fails CLOSED — an absent row
 * hides the dialog.
 */
export function isGetStartedDialogEnabled(): boolean {
    return gpHasFeature(GET_STARTED_DIALOG);
}

/**
 * Whether the schedule reveal animation is available — gates every way to
 * start it: the toolbar Play button, the Schedule Animation video export,
 * and the post-generation intro card. Shipped ungated (PRs #207/#221) and
 * pulled behind this flag. Fails CLOSED — an absent row hides it all.
 */
export function isPlanRevealAnimationEnabled(): boolean {
    return gpHasFeature(PLAN_REVEAL_ANIMATION);
}

const BASELINES = 'Baselines';

/**
 * Whether schedule baselines are available: the Baselines dialog on the Plan
 * menu and, in later slices, baseline bars and variance columns. Fails
 * CLOSED — an absent row hides the feature (production ships with the flag
 * off until the v1 slices are complete; the dev server enables every
 * well-known flag locally).
 */
export function isBaselinesEnabled(): boolean {
    return gpHasFeature(BASELINES);
}

const CAPABILITY_GATING = 'CapabilityGating';

/**
 * Whether tier-based capability gating is active for this user (issue #563) —
 * the browser's read of the same master switch the server resolves
 * capabilities through.
 *
 * Fails CLOSED on the gate, which is fail-OPEN on capabilities: an absent row
 * means nothing is withheld. Deliberate, and the same asymmetry the server
 * side documents on WellKnownFlags.CapabilityGating — #510's tier model has
 * not shipped, so enforcing its matrix today would take capabilities away from
 * users who currently have them.
 *
 * The browser consumers are the `gp-capability` gating directives and
 * `gpCapabilityUpsell` (issue #566): with this off, no control is withheld and
 * no action is refused. The check is belt and braces rather than the only lock
 * — the SERVER resolves every capability to its ungated value under the same
 * flag, so a page whose flags and capabilities came from one render cannot
 * disagree with itself about whether gating is on.
 */
export function isCapabilityGatingEnabled(): boolean {
    return gpHasFeature(CAPABILITY_GATING);
}

const PLAN_CALENDAR_VIEW = 'PlanCalendarView';

/**
 * Whether the plan calendar view is available (issue #636) — gates every way
 * in: the workspace toolbar Calendar button, the Reports menu entry, the
 * share dialog's published calendar link, and the calendar report surface
 * itself. Fails CLOSED — unreleased, so an absent row keeps it dark.
 */
export function isPlanCalendarViewEnabled(): boolean {
    return gpHasFeature(PLAN_CALENDAR_VIEW);
}
